/**
 * geometryUtils.ts
 *
 * Shared lat/lon <-> 3D position conventions used by every scene module, so
 * the terrain mesh, atmosphere shells, particles and coastline overlay all
 * agree on where a given (lat, lon) point sits in space.
 */

import * as THREE from "three";

const DEG2RAD = Math.PI / 180;

/** Converts (lat, lon) in degrees to a point on the unit sphere. */
export function latLonToUnitVector(
  latDeg: number,
  lonDeg: number,
  target: THREE.Vector3 = new THREE.Vector3()
): THREE.Vector3 {
  const phi = (90 - latDeg) * DEG2RAD; // colatitude, 0 at north pole
  const theta = (lonDeg + 180) * DEG2RAD; // azimuth
  const sinPhi = Math.sin(phi);
  target.set(-sinPhi * Math.cos(theta), Math.cos(phi), sinPhi * Math.sin(theta));
  return target;
}

export interface ReducedGridOptions {
  /** Number of latitude rows from the south pole to the north pole. */
  latSegments: number;
  /** Longitude segment count at the equator (the densest row). */
  equatorLonSegments: number;
  /** Minimum longitude segment count, enforced near the poles. */
  minLonSegments: number;
  /** >1 clusters more latitude rows near the equator; 1 = uniform row spacing. */
  equatorialBias: number;
}

export interface ReducedLatLonGrid {
  /** Unit-sphere (radius 1) positions, to be scaled/displaced per vertex by the caller. */
  positions: Float32Array;
  /** Interleaved [latDeg, lonDeg] per vertex, for CPU-side field sampling. */
  latLonPairs: Float32Array;
  indices: Uint32Array;
  vertexCount: number;
  triangleCount: number;
}

/**
 * Builds a "reduced" lat/lon sphere grid: both the number of latitude rows
 * near the equator and the number of longitude samples per row (relative to
 * cos(latitude)) increase toward the equator and taper toward the poles.
 * This avoids the classic UV-sphere problem where a uniform-degree grid
 * wastes enormous vertex density near the poles (where meridians converge)
 * while under-resolving the equator (where each degree of longitude spans
 * the most ground distance). Real global weather models use the same
 * "reduced Gaussian grid" idea for the same reason.
 *
 * Adjacent rows with different vertex counts are stitched using a
 * merge-by-angular-position walk, which works for any pair of row sizes
 * (not just power-of-two ratios).
 */
export function buildReducedLatLonGrid(options: ReducedGridOptions): ReducedLatLonGrid {
  const { latSegments, equatorLonSegments, minLonSegments, equatorialBias } = options;

  const rowLatDeg: number[] = [];
  for (let i = 0; i <= latSegments; i++) {
    const t = -1 + (2 * i) / latSegments; // -1..1, uniform
    const biased = Math.sign(t) * Math.pow(Math.abs(t), equatorialBias); // clustered toward 0
    rowLatDeg.push(biased * 90);
  }

  const rowLonSegments = rowLatDeg.map((latDeg) => {
    const scale = Math.cos(latDeg * DEG2RAD);
    return Math.max(minLonSegments, Math.round(equatorLonSegments * scale));
  });

  const rowStartIndex: number[] = [];
  const positionsList: number[] = [];
  const latLonList: number[] = [];
  const tmp = new THREE.Vector3();

  for (let r = 0; r <= latSegments; r++) {
    rowStartIndex.push(positionsList.length / 3);
    const latDeg = rowLatDeg[r];
    const nLon = rowLonSegments[r];
    for (let c = 0; c < nLon; c++) {
      const lonDeg = -180 + (360 * c) / nLon;
      latLonToUnitVector(latDeg, lonDeg, tmp);
      positionsList.push(tmp.x, tmp.y, tmp.z);
      latLonList.push(latDeg, lonDeg);
    }
  }

  const indices: number[] = [];
  for (let r = 0; r < latSegments; r++) {
    const startA = rowStartIndex[r];
    const nA = rowLonSegments[r];
    const startB = rowStartIndex[r + 1];
    const nB = rowLonSegments[r + 1];

    let i = 0;
    let j = 0;
    while (i < nA || j < nB) {
      const fracA = i / nA;
      const fracB = j / nB;
      if (j >= nB || (i < nA && fracA <= fracB)) {
        const a0 = startA + i;
        const a1 = startA + ((i + 1) % nA);
        const b0 = startB + (j % nB);
        indices.push(a0, a1, b0);
        i++;
      } else {
        const a0 = startA + (i % nA);
        const b0 = startB + j;
        const b1 = startB + ((j + 1) % nB);
        indices.push(a0, b0, b1);
        j++;
      }
    }
  }

  return {
    positions: Float32Array.from(positionsList),
    latLonPairs: Float32Array.from(latLonList),
    indices: Uint32Array.from(indices),
    vertexCount: positionsList.length / 3,
    triangleCount: indices.length / 3
  };
}
